/**
 * ChronologyTab — the Chronology Builder.
 *
 * The user builds a curated timeline by hand: the event Browser is the front
 * door (filter the case's extracted events, select — one or all — and Add),
 * plus manual entries. Arc assists by suggesting candidate dates for undated
 * entries (user picks). Entries are edit/reorder/place/export-able. Saved
 * server-side on every action (no optimistic local state yet — actions hit the
 * API then refetch).
 *
 * Lazy-mounted (no background polling — every action is a synchronous endpoint).
 * See plans/optimized-bouncing-falcon.md.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api';
import { useUserProfile } from '../../hooks/useUserProfile';
import useHelpTarget from '../../hooks/useHelpTarget';
import ChronologyRefineDrawer from '../../components/ChronologyRefineDrawer';
import ChronologyBuildLane from '../../components/ChronologyBuildLane';
import BackgroundJobsBanner from '../../components/BackgroundJobsBanner';
import {
  partitionEntries,
  formatEntryDate,
  formatDate,
  entryCitation,
  participantName,
} from '../../domain/chronologyEntry';
import {
  computeFacets,
  filterEvents,
  eventParticipantNames,
} from '../../domain/chronologyEvents';
import { isPresentableParticipant } from '../../domain/personDisplay';

const S = {
  wrap: { padding: '8px 4px', maxWidth: 980 },
  bar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 },
  btn: { padding: '6px 12px', borderRadius: 6, border: '1px solid #c7ccd6', background: '#fff', cursor: 'pointer', fontSize: 13 },
  btnPrimary: { padding: '6px 12px', borderRadius: 6, border: '1px solid #2f6df6', background: '#2f6df6', color: '#fff', cursor: 'pointer', fontSize: 13 },
  input: { padding: '6px 8px', borderRadius: 6, border: '1px solid #c7ccd6', fontSize: 13 },
  dateHdr: { fontWeight: 700, fontSize: 13, color: '#475', margin: '18px 0 6px', borderBottom: '1px solid #eef0f4', paddingBottom: 4 },
  row: { border: '1px solid #e6e9ef', borderRadius: 8, padding: '10px 12px', marginBottom: 8, background: '#fff' },
  rowSuggested: { borderStyle: 'dashed', borderColor: '#b89020', background: '#fffdf5' },
  pill: { fontSize: 11, padding: '1px 7px', borderRadius: 10, marginLeft: 8, verticalAlign: 'middle' },
  meta: { fontSize: 12, color: '#778', marginTop: 4 },
  rowActions: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  tray: { marginTop: 24, padding: '12px', border: '1px dashed #c7ccd6', borderRadius: 8, background: '#fafbfc' },
  empty: { padding: 40, textAlign: 'center', color: '#778' },
  link: { color: '#2f6df6', textDecoration: 'none', fontSize: 12 },
  err: { color: '#c0392b', fontSize: 13, margin: '8px 0' },
  suggestBox: { marginTop: 8, padding: '8px 10px', borderRadius: 6, background: '#eef5ff', border: '1px solid #bcd6ff' },
  suggestQuote: { fontSize: 12, color: '#556', fontStyle: 'italic', marginTop: 4 },
  suggestRationale: { fontSize: 12, color: '#667', marginTop: 2 },
  arcNotes: { marginTop: 8, padding: '8px 10px', borderRadius: 6, background: '#f7f3ff', border: '1px solid #d9cdf5' },
  arcNotesHead: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  arcNotesMeta: { fontSize: 11, color: '#8a7fae', fontWeight: 400 },
  panel: { marginTop: 16, border: '1px solid #d6dae3', borderRadius: 10, background: '#fff', overflow: 'hidden' },
  panelHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f6f8fc', borderBottom: '1px solid #e6e9ef', flexWrap: 'wrap' },
  panelBody: { display: 'flex', gap: 12, padding: 12, maxHeight: 460, overflow: 'hidden' },
  facetCol: { width: 220, flexShrink: 0, overflowY: 'auto', maxHeight: 436, paddingRight: 6, borderRight: '1px solid #eef0f4' },
  facetHdr: { fontWeight: 700, fontSize: 11, color: '#778', textTransform: 'uppercase', margin: '10px 0 4px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 14, border: '1px solid #c7ccd6', background: '#fff', cursor: 'pointer', fontSize: 12, margin: '0 4px 4px 0' },
  chipOn: { borderColor: '#2f6df6', background: '#e8f0ff', color: '#1a4fc0' },
  chipCount: { fontSize: 11, color: '#99a', fontWeight: 600 },
  evList: { flex: 1, overflowY: 'auto', maxHeight: 436 },
  evRow: { display: 'flex', gap: 8, padding: '8px 6px', borderBottom: '1px solid #f0f2f6', fontSize: 13 },
  linkBtn: { background: 'none', border: 'none', color: '#2f6df6', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline', fontWeight: 600 },
  menu: { position: 'absolute', right: 0, top: '112%', background: '#fff', border: '1px solid #c7ccd6', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', zIndex: 20, minWidth: 184, overflow: 'hidden' },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', borderBottom: '1px solid #f0f2f6', background: '#fff', cursor: 'pointer', fontSize: 13 },
  evMeta: { fontSize: 12, color: '#778', marginTop: 2 },
};

const CONF_LABEL = { high: 'high confidence', medium: 'medium confidence', low: 'low confidence' };

function Pill({ text, color, bg }) {
  return <span style={{ ...S.pill, color, background: bg }}>{text}</span>;
}

/* Who-was-there mini network: the event as a diamond hub, participants as
 * spokes — pure client-side SVG from the names already on the entry (no
 * fetch). Clicking it (or the 🕸 button) opens the full Graph Explorer
 * seeded on this entry's source event. */
function MiniCast({ names, onOpen }) {
  const shown = names.slice(0, 8);
  const W = 250;
  const H = 34 + Math.ceil(shown.length / 2) * 26;
  const cx = 40;
  const cy = H / 2;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ display: 'block', marginTop: 6, cursor: onOpen ? 'pointer' : 'default' }}
      onClick={onOpen}
      role="img"
      aria-label={`Participants: ${shown.join(', ')}`}
    >
      {shown.map((n, i) => {
        const y = 16 + i * ((H - 28) / Math.max(shown.length - 1, 1));
        return (
          <g key={i}>
            <line x1={cx} y1={cy} x2={cx + 34} y2={y} stroke="#b9c6e4" strokeWidth="1" strokeDasharray="2 3" />
            <circle cx={cx + 34} cy={y} r="3.5" fill="#2f6df6" opacity="0.75" />
            <text x={cx + 42} y={y + 3.5} fontSize="10" fill="#556">
              {n.length > 24 ? `${n.slice(0, 23)}…` : n}
            </text>
          </g>
        );
      })}
      <rect x={cx - 7} y={cy - 7} width="14" height="14" fill="#eef2fb" stroke="#7a8fc9" transform={`rotate(45 ${cx} ${cy})`} />
      {names.length > shown.length && (
        <text x={cx - 10} y={H - 4} fontSize="9" fill="#99a">+{names.length - shown.length} more</text>
      )}
    </svg>
  );
}

/* Arc's notes — the advisory enrichment panel. Visibly agent-generated and
 * kept apart from the user's own fields; the export paths never read it.
 * "Apply" copies a piece into the entry's real note/description through the
 * normal edit path (which marks the entry user_edited) and then ARCHIVES the
 * panel — once the user has taken what they want, the notes collapse to a
 * one-line chip (kept for later, not deleted). Dismiss archives without
 * applying; Re-enrich writes fresh notes and un-archives. */
function ArcNotes({ entry, onApplyNotes, onDismissNotes, onRefine, busy }) {
  // null → follow the archived state; true/false → user toggled this render.
  const [override, setOverride] = useState(null);
  const e = entry.enrichment;
  if (!e) return null;
  const archived = !!e.dismissed_at;
  const open = override === null ? !archived : override;
  // Real UTC instant (not a case-local document time) → localize normally.
  const when = e.generated_at
    ? new Date(e.generated_at).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
    : '';
  const applyNote = () => {
    setOverride(null);
    onApplyNotes(entry, { note: `${entry.note ? `${entry.note}\n\n` : ''}${e.summary}` });
  };
  const applyDescription = () => {
    setOverride(null);
    onApplyNotes(entry, { description: e.summary });
  };
  return (
    <div style={S.arcNotes}>
      <div style={S.arcNotesHead} onClick={() => setOverride(!open)}>
        <span>✨ Arc’s notes{archived ? ' (archived)' : ''}</span>
        <span style={S.arcNotesMeta}>
          {archived ? 'click to view' : `${when ? `generated ${when} · ` : ''}not exported until you apply`}
        </span>
        <span style={{ flex: 1 }} />
        <span>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6 }}>
          {e.summary && <div style={{ fontSize: 13 }}>{e.summary}</div>}
          {(e.participants || []).length > 0 && (
            <div style={S.meta}>People: {e.participants.join(', ')}</div>
          )}
          {(e.key_quotes || []).map((q, i) => (
            <div key={i} style={S.suggestQuote}>“{q.text}”</div>
          ))}
          {(e.date_observations || []).length > 0 && (
            <div style={S.meta}>Dates mentioned: {e.date_observations.join(' · ')}</div>
          )}
          <div style={S.rowActions}>
            {e.summary && (
              <>
                <button style={S.btn} onClick={applyNote} disabled={busy}>Add summary to note</button>
                <button
                  style={S.btn}
                  onClick={applyDescription}
                  disabled={busy}
                  title="Replaces the entry description with Arc’s summary"
                >Use as description</button>
              </>
            )}
            {onRefine && (
              <button
                style={S.btn}
                onClick={() => onRefine(entry)}
                disabled={busy}
                title="Open a conversation with Arc about this event — refine the details together, then apply the change to the card"
              >✨ Refine with Arc</button>
            )}
            {!archived && (
              <button
                style={S.btn}
                onClick={() => { setOverride(null); onDismissNotes(entry); }}
                disabled={busy}
                title="Collapse these notes to a chip on this card (kept, not deleted)"
              >Dismiss</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* Preserved original accounts from a merge (perspectives kept, independently
 * citable) — the expandable "N source accounts" section on a merged card. */
function LineageAccounts({ accounts, label }) {
  const [open, setOpen] = useState(false);
  if (!accounts?.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <button style={{ ...S.linkBtn, fontSize: 12 }} onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} {accounts.length === 1 ? label.one : `${accounts.length} ${label.many}`}
      </button>
      {open && accounts.map((a, i) => (
        <div key={a.entry_id || i} style={{ margin: '4px 0 4px 14px', padding: '6px 8px', borderLeft: '2px solid #d6dae3', fontSize: 12.5, background: '#fafbfc' }}>
          <strong>{a.title || '(untitled)'}</strong>
          {a.effective_date && <span style={{ color: '#778' }}> · {formatDate(a.effective_date, a.date_only, a.date_precision)}</span>}
          {a.description && <div style={{ marginTop: 2 }}>{a.description}</div>}
          {a.grounded_quote && <div style={S.suggestQuote}>“{a.grounded_quote}”</div>}
        </div>
      ))}
    </div>
  );
}

function EntryRow({
  entry, pathname, onAccept, onReject, onDelete, onEdit,
  onSuggestDate, onAcceptPlacement, onRejectPlacement,
  onArcScan, onEnrich, onApplyNotes, onDismissNotes, onRefine, busy, graphOn,
  selectMode, selected, onToggleSelect, onSplit, onUnsplit, onUnmerge,
  onPlace, onUnpin, onMove, onNest, onUnnest, titleById,
  childCount = 0, collapsed = false, onToggleCollapse,
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(entry.title || '');
  const [note, setNote] = useState(entry.note || '');
  const [dateStr, setDateStr] = useState(
    entry.effective_date ? String(entry.effective_date).slice(0, 10) : '',
  );
  const [precision, setPrecision] = useState(entry.date_precision || 'day');
  const suggested = entry.review_state === 'suggested';
  const isUndated = !entry.effective_date;
  const candidates = (isUndated && Array.isArray(entry.suggested_dates)) ? entry.suggested_dates : [];
  const hasSuggestion = candidates.length > 0;
  const cite = entryCitation(entry, pathname);
  const parts = (entry.participants || []).map(participantName).filter(Boolean);
  const mergedAccounts = entry.lineage?.merged_accounts || [];
  const absorbedSummaries = entry.lineage?.absorbed_summaries || [];
  const splitFrom = entry.lineage?.split_from || null;
  const isPinned = isUndated && !!entry.anchor_entry_id
    && (entry.placement ? entry.placement === 'pinned' : true);
  const pinnedBy = entry.anchor_origin === 'arc' ? 'by Arc'
    : entry.anchor_origin === 'split' ? 'from split' : 'by you';

  const saveEdit = () => {
    onEdit(entry, {
      title: title.trim(),
      note,
      effective_date: dateStr ? `${dateStr}T00:00:00Z` : null,
      clear_date: !dateStr,
      date_only: true,
      date_precision: precision,
    });
    setEditing(false);
  };

  return (
    <div style={{ ...S.row, ...(suggested ? S.rowSuggested : null), ...(selectMode && selected ? { borderColor: '#2f6df6', background: '#f4f8ff' } : null) }}>
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input style={S.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={S.input} type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
            <select
              style={S.input}
              value={precision}
              onChange={(e) => setPrecision(e.target.value)}
              title="How precise this date really is — 'sometime in March 2024' renders as Mar 2024, not a falsely exact day"
            >
              <option value="day">Exact day</option>
              <option value="month">Month only</option>
              <option value="year">Year only</option>
            </select>
          </div>
          <textarea style={{ ...S.input, minHeight: 48 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" />
          <div style={S.rowActions}>
            <button style={S.btnPrimary} onClick={saveEdit} disabled={busy}>Save</button>
            <button style={S.btn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div>
            {selectMode && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect(entry)}
                style={{ marginRight: 8, verticalAlign: 'middle' }}
              />
            )}
            {onToggleCollapse && (
              <button
                style={{ ...S.linkBtn, textDecoration: 'none', marginRight: 6, fontWeight: 700 }}
                onClick={onToggleCollapse}
                title={collapsed ? `Show the ${childCount} events inside` : 'Collapse to the episode line'}
              >{collapsed ? '▸' : '▾'}</button>
            )}
            <strong>{entry.title || '(untitled event)'}</strong>
            {childCount > 0 && (
              <Pill
                text={`episode · ${childCount} event${childCount === 1 ? '' : 's'}`}
                color="#5b3fa8" bg="#efe8fd"
              />
            )}
            {entry.event_type && <Pill text={String(entry.event_type).replace(/_/g, ' ').toLowerCase()} color="#456" bg="#eef2fb" />}
            {suggested && <Pill text="suggested" color="#7a5b00" bg="#fdf2cf" />}
            {/* "manual" = the user typed this entry; an event picked from the
                browser (or accepted from a build) is extracted content the
                user merely approved — label it honestly. */}
            {entry.provenance === 'user_added' && !entry.graph_event_id
              && <Pill text="manual" color="#2c6e49" bg="#e6f4ec" />}
            {entry.graph_event_id && entry.provenance !== 'user_edited' && !suggested
              && <Pill text="extracted" color="#456" bg="#eef2fb" />}
            {/* Arc drafted this from a source's text (no graph event behind it)
                — distinct from graph "extracted" and hand-typed "manual". */}
            {!entry.graph_event_id && entry.provenance === 'agent_suggested'
              && (entry.source_document_ids || []).length > 0
              && <Pill text="drafted from source" color="#5b3fa8" bg="#efe8fd" />}
            {entry.parent_entry_id && entry.lineage?.nested?.by === 'arc' && (
              <span title={entry.lineage.nested.rationale || 'Filed under this episode by the organize pass — Un-nest if wrong'}>
                <Pill text="✨ filed by Arc" color="#5b3fa8" bg="#efe8fd" />
              </span>
            )}
            {entry.ruling && (
              <span title={entry.ruling.assertion}>
                <Pill
                  text={entry.ruling.review_state === 'HUMAN_CONFIRMED' ? '✔ corrected' : '🤖 suggested'}
                  color={entry.ruling.review_state === 'HUMAN_CONFIRMED' ? '#16825d' : '#4f46e5'}
                  bg={entry.ruling.review_state === 'HUMAN_CONFIRMED' ? '#e0f2ea' : '#eceafd'}
                />
              </span>
            )}
          </div>
          {entry.description && <div style={{ fontSize: 13, marginTop: 4 }}>{entry.description}</div>}
          {entry.ruling?.review_state === 'HUMAN_CONFIRMED' && (
            <div style={{ fontSize: 12.5, marginTop: 4, color: '#16825d' }}
              title="Your correction — it overrides the system's original reading">
              ✔ {entry.ruling.assertion}
            </div>
          )}
          {isPinned && (
            <div style={{ fontSize: 12, color: '#6b5bd6', marginTop: 4 }}>
              📌 undated — placed {entry.anchor_side === 'before' ? 'before' : 'after'}{' '}
              “{titleById?.[entry.anchor_entry_id] || 'a removed entry'}” ({pinnedBy})
              {onUnpin && (
                <button style={{ ...S.linkBtn, fontSize: 12, marginLeft: 8 }} onClick={() => onUnpin(entry)} disabled={busy}>
                  Unpin
                </button>
              )}
            </div>
          )}
          <div style={S.meta}>
            {formatEntryDate(entry) || (isPinned ? null : 'Undated')}
            {parts.length > 0 && <> · {parts.join(', ')}</>}
            {cite && <> · <a
              href={cite.href}
              style={S.link}
              onClick={(e) => {
                // In-app nav (like ChatMessage citations): a full reload
                // re-mounts AuthProvider and the viewer fetch 401s before the
                // token is ready, which signs the user out. navigate() opens
                // the viewer overlay without a reload. Preserve modifier-clicks.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate(cite.href);
              }}
            >📄 {cite.label}</a></>}
            {/* Corroboration cue: an extracted claim seen in one document
                reads differently from one seen in three. Provenance only —
                a single-source claim can still be a misread form field. */}
            {(entry.source_document_ids || []).length > 0 && (
              <span title="How many documents this event was extracted from — a single-source claim deserves more scrutiny">
                {' · '}{entry.source_document_ids.length === 1
                  ? 'single source'
                  : `${entry.source_document_ids.length} sources`}
              </span>
            )}
          </div>
          {entry.note && <div style={{ ...S.meta, fontStyle: 'italic' }}>📝 {entry.note}</div>}
          {graphOn && parts.length >= 2 && (
            <MiniCast
              names={parts}
              onOpen={entry.graph_event_id
                ? () => navigate(`${pathname}?graph=ev:${entry.graph_event_id}`)
                : undefined}
            />
          )}
          {hasSuggestion && (
            <div style={S.suggestBox}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                ✨ Arc found {candidates.length === 1 ? 'a possible date' : `${candidates.length} possible dates`} — pick the right one:
              </div>
              {candidates.map((c, i) => (
                <div key={i} style={{ padding: '6px 0', borderTop: i ? '1px solid #dbe6fb' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button style={S.btnPrimary} onClick={() => onAcceptPlacement(entry, c)} disabled={busy}>
                      Use {formatDate(c.date, c.date_only)}
                    </button>
                    {c.origin === 'arc' && <Pill text="✨ Arc" color="#5b3fa8" bg="#efe8fd" />}
                    {c.confidence && <span style={{ fontSize: 12, color: '#778' }}>{CONF_LABEL[c.confidence] || c.confidence}</span>}
                  </div>
                  {c.rationale && <div style={S.suggestRationale}>{c.rationale}</div>}
                  {c.quote && <div style={S.suggestQuote}>“{c.quote}”</div>}
                </div>
              ))}
              <div style={S.rowActions}>
                <button style={S.btn} onClick={() => onRejectPlacement(entry)} disabled={busy}>None of these</button>
              </div>
            </div>
          )}
          <LineageAccounts
            accounts={mergedAccounts}
            label={{ one: '1 source account (merged)', many: 'source accounts (merged)' }}
          />
          <LineageAccounts
            accounts={absorbedSummaries}
            label={{ one: 'absorbed summary', many: 'absorbed summaries' }}
          />
          <ArcNotes entry={entry} onApplyNotes={onApplyNotes} onDismissNotes={onDismissNotes} onRefine={onRefine} busy={busy} />
          <div style={S.rowActions}>
            {suggested && <button style={S.btnPrimary} onClick={() => onAccept(entry)} disabled={busy}>Accept</button>}
            {suggested && <button style={S.btn} onClick={() => onReject(entry)} disabled={busy}>Reject</button>}
            {onMove && (
              <>
                <button style={S.btn} title="Move up within this day" onClick={() => onMove(entry, -1)} disabled={busy}>↑</button>
                <button style={S.btn} title="Move down within this day" onClick={() => onMove(entry, 1)} disabled={busy}>↓</button>
              </>
            )}
            {isUndated && !isPinned && onPlace && (
              <button
                style={S.btn}
                title="Place this undated entry in the flow — immediately before or after another entry"
                onClick={() => onPlace(entry)}
                disabled={busy}
              >📌 Place…</button>
            )}
            {isUndated && !hasSuggestion && onSuggestDate && (
              <button style={S.btn} onClick={() => onSuggestDate(entry)} disabled={busy}>✨ Suggest date</button>
            )}
            {isUndated && onArcScan && (
              <button
                style={S.btn}
                title="Arc reads this entry’s source documents and proposes dates with supporting quotes (small metered cost)"
                onClick={() => onArcScan(entry)}
                disabled={busy}
              >✨ Ask Arc for a date</button>
            )}
            {onEnrich && (
              <button
                style={S.btn}
                title="Arc reads this entry’s sources and writes cited notes on this card (small metered cost)"
                onClick={() => onEnrich(entry)}
                disabled={busy}
              >{entry.enrichment ? '✨ Re-enrich' : '✨ Enrich'}</button>
            )}
            {onRefine && entry.enrichment && (
              <button
                style={S.btn}
                title="Open a conversation with Arc about this event — refine the details together, then apply the change to the card"
                onClick={() => onRefine(entry)}
                disabled={busy}
              >✨ Refine with Arc</button>
            )}
            {graphOn && entry.graph_event_id && (
              <button
                style={S.btn}
                title="Open this moment on the graph — who was there and how they relate"
                onClick={() => navigate(`${pathname}?graph=ev:${entry.graph_event_id}`)}
              >🕸 Who was there</button>
            )}
            {onSplit && (
              <button
                style={S.btn}
                title="Split this entry into separate sequential events (undoable)"
                onClick={() => onSplit(entry)}
                disabled={busy}
              >Split…</button>
            )}
            {onNest && !entry.parent_entry_id && (
              <button
                style={S.btn}
                title="Nest this entry inside a broader episode (display hierarchy — nothing merges; undoable)"
                onClick={() => onNest(entry)}
                disabled={busy}
              >Nest under…</button>
            )}
            {entry.parent_entry_id && onUnnest && (
              <button
                style={S.btn}
                title="Un-nest: back to the top level"
                onClick={() => onUnnest(entry)}
                disabled={busy}
              >Un-nest</button>
            )}
            {(mergedAccounts.length > 0 || absorbedSummaries.length > 0) && onUnmerge && (
              <button
                style={S.btn}
                title="Undo the merge: restore the absorbed entries and revert this card"
                onClick={() => onUnmerge(entry)}
                disabled={busy}
              >Unmerge</button>
            )}
            {splitFrom && onUnsplit && (
              <button
                style={S.btn}
                title={`Undo the split: restore “${splitFrom.title || 'the original entry'}” and remove its parts`}
                onClick={() => onUnsplit(entry)}
                disabled={busy}
              >Unsplit</button>
            )}
            <button style={S.btn} onClick={() => setEditing(true)}>Edit</button>
            <button style={S.btn} onClick={() => onDelete(entry)} disabled={busy}>Delete</button>
          </div>
        </>
      )}
    </div>
  );
}

/* One entry + its nested children (containment tree). The parent renders as
 * an episode with a left rail spanning its children — broad left, specific
 * right; horizontal position means containment and nothing else. Collapse
 * folds the block to its episode line (semantic zoom). */
function EntryNode({ entry, childrenOf, selectedIds, onMove, ...handlers }) {
  const kids = childrenOf?.[entry.id] || [];
  const [open, setOpen] = useState(true);
  return (
    <div>
      <EntryRow
        entry={entry}
        {...handlers}
        selected={selectedIds?.has(entry.id)}
        onMove={onMove}
        childCount={kids.length}
        collapsed={!open}
        onToggleCollapse={kids.length ? () => setOpen((o) => !o) : undefined}
      />
      {open && kids.length > 0 && (
        <div style={{ marginLeft: 16, paddingLeft: 12, borderLeft: '3px solid #d9cdf5', marginBottom: 8 }}>
          {kids.map((k) => (
            <EntryNode
              key={k.id}
              entry={k}
              childrenOf={childrenOf}
              selectedIds={selectedIds}
              {...handlers}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UndatedTray({ entries, onSuggestAll, selectedIds, ...handlers }) {
  if (!entries.length) return null;
  const needSuggest = entries.filter((e) => !e.suggested_date).length;
  return (
    <div style={S.tray}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          Undated tray ({entries.length}) — set a date to place on the timeline
        </span>
        <span style={{ flex: 1 }} />
        {needSuggest > 0 && (
          <button style={S.btn} onClick={onSuggestAll} disabled={handlers.busy}>
            ✨ Suggest dates ({needSuggest})
          </button>
        )}
      </div>
      {entries.map((e) => (
        <EntryRow key={e.id} entry={e} {...handlers} selected={selectedIds?.has(e.id)} />
      ))}
    </div>
  );
}

function FacetChip({ label, count, active, onClick }) {
  return (
    <button style={{ ...S.chip, ...(active ? S.chipOn : null) }} onClick={onClick}>
      <span>{label}</span>
      <span style={S.chipCount}>{count}</span>
    </button>
  );
}

/* Detail pane behind a row's ▸ button — what the event actually is (full
 * description, participants, and every claim about it grouped by asserter)
 * BEFORE the user adds it to the timeline. Lazily fetched, cached per event
 * for the browser's lifetime. */
function EventDetailPane({ detail, pathname }) {
  const navigate = useNavigate();
  if (!detail) return <div style={{ ...S.evMeta, padding: '6px 0 8px 24px' }}>Loading details…</div>;
  if (detail.error) return <div style={{ ...S.evMeta, padding: '6px 0 8px 24px', color: '#c0392b' }}>{detail.error}</div>;
  const ev = detail.results?.event || {};
  const groups = detail.results?.claims_by_asserter || [];
  const openDoc = (docId, chunkId) => (e) => {
    e.preventDefault();
    navigate(`${pathname}?doc=${docId}${chunkId ? `&chunk=${chunkId}` : ''}`);
  };
  return (
    <div style={{ margin: '4px 0 8px 24px', padding: '8px 10px', borderRadius: 6, background: '#f8f9fc', border: '1px solid #e6e9ef', fontSize: 12.5 }}>
      {ev.description && <div style={{ marginBottom: 4 }}>{ev.description}</div>}
      <div style={S.evMeta}>
        {ev.event_type && <>{String(ev.event_type).replace(/_/g, ' ').toLowerCase()}</>}
        {ev.confidence_bucket && <> · {String(ev.confidence_bucket).toLowerCase()} confidence</>}
        {(ev.participants || []).length > 0 && (
          <> · {(ev.participants || []).map((p) => p.canonical_name).filter(Boolean).join(', ')}</>
        )}
        {ev.location?.canonical_name && <> · 📍 {ev.location.canonical_name}</>}
      </div>
      {groups.length > 0 ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: '#778', textTransform: 'uppercase' }}>
            Who says this happened
          </div>
          {groups.map((g, i) => (
            <div key={g.asserter?.entity_id || i} style={{ marginTop: 4 }}>
              <span style={{ fontWeight: 600 }}>{g.asserter?.canonical_name || 'Unnamed source'}</span>
              {(g.claims || []).slice(0, 4).map((c, j) => (
                <div key={c.claim_id || j} style={{ padding: '2px 0 2px 10px' }}>
                  {c.claim_text}
                  {c.supporting_quote && <div style={S.suggestQuote}>“{c.supporting_quote}”</div>}
                  {c.filename && (
                    <a href="#" style={S.link} onClick={openDoc(c.document_id, c.source_chunk_id)}>
                      📄 {c.filename}
                    </a>
                  )}
                </div>
              ))}
              {(g.claims || []).length > 4 && (
                <div style={{ ...S.evMeta, paddingLeft: 10 }}>+{g.claims.length - 4} more from this source</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...S.evMeta, marginTop: 4 }}>No individual claims recorded about this event.</div>
      )}
    </div>
  );
}

function EventBrowser({ events, loading, busy, onAdd, onClose, onFetchDetail, pathname }) {
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState(() => new Set());
  const [participants, setParticipants] = useState(() => new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showGenericParticipants, setShowGenericParticipants] = useState(false);
  // Detail pane: which row is expanded + a per-event cache (fetch once per
  // browser lifetime; no prefetch over the 3000-row list).
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});

  const toggleDetail = (ev) => {
    const id = ev.event_id;
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!details[id] && onFetchDetail) {
      onFetchDetail(id)
        .then((d) => setDetails((m) => ({ ...m, [id]: d })))
        .catch((e) => setDetails((m) => ({ ...m, [id]: { error: e.message || 'Failed to load details' } })));
    }
  };

  const facets = useMemo(() => computeFacets(events), [events]);
  // Real names get top billing; pronouns/objects ("I", "bones", "his truck")
  // fold behind an explicit toggle — see domain/personDisplay.js.
  const { presentableParticipants, genericParticipants } = useMemo(() => {
    const presentable = [];
    const generic = [];
    for (const f of facets.participants) {
      (isPresentableParticipant(f.value) ? presentable : generic).push(f);
    }
    return { presentableParticipants: presentable, genericParticipants: generic };
  }, [facets.participants]);
  const filtered = useMemo(
    () => filterEvents(events, { query, types, participants, dateFrom, dateTo, onlyMissing }),
    [events, query, types, participants, dateFrom, dateTo, onlyMissing],
  );

  const toggleIn = (set, setter, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };
  const toggleSelect = (ev) => {
    if (ev.already_in_chronology) return;
    toggleIn(selected, setSelected, ev.event_id);
  };
  const selectableFiltered = filtered.filter((e) => !e.already_in_chronology);
  const allFilteredSelected = selectableFiltered.length > 0
    && selectableFiltered.every((e) => selected.has(e.event_id));
  const toggleSelectAll = () => {
    const next = new Set(selected);
    if (allFilteredSelected) selectableFiltered.forEach((e) => next.delete(e.event_id));
    else selectableFiltered.forEach((e) => next.add(e.event_id));
    setSelected(next);
  };

  const missingCount = filtered.filter((e) => !e.already_in_chronology).length;

  // Provisional containment (server-inferred: temporal span + shared
  // participants): children indent under their broad parent — broad left,
  // specific right. Labeled inferred; Add carries the structure into the
  // chronology where the user confirms or re-parents it.
  const displayRows = useMemo(() => {
    const ids = new Set(filtered.map((e) => String(e.event_id)));
    const kids = {};
    const roots = [];
    for (const ev of filtered) {
      const pid = ev.group_parent_event_id;
      if (pid && ids.has(String(pid)) && String(pid) !== String(ev.event_id)) {
        (kids[String(pid)] = kids[String(pid)] || []).push(ev);
      } else {
        roots.push(ev);
      }
    }
    const out = [];
    const emit = (ev, depth) => {
      const children = kids[String(ev.event_id)] || [];
      out.push({ ev, depth, kidCount: children.length });
      if (depth < 2) children.forEach((k) => emit(k, depth + 1));
      else children.forEach((k) => out.push({ ev: k, depth, kidCount: 0 }));
    };
    roots.forEach((r) => emit(r, 0));
    return out;
  }, [filtered]);

  return (
    <div style={S.panel}>
      <div style={S.panelHead}>
        <strong style={{ fontSize: 14 }}>Browse case events</strong>
        <input
          style={{ ...S.input, width: 220 }}
          placeholder="Search description / participant…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label
          style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
          title="Show only events that are NOT yet in this chronology, so you can see what's left to add"
        >
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          Hide events already in this chronology
        </label>
        <input style={{ ...S.input, width: 132 }} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From date" />
        <input style={{ ...S.input, width: 132 }} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To date" />
        {(dateFrom || dateTo) && (
          <button style={S.btn} onClick={() => { setDateFrom(''); setDateTo(''); }} title="Clear the date range">Clear dates</button>
        )}
        <span style={{ flex: 1 }} />
        <button style={S.btn} onClick={onClose}>Close</button>
      </div>

      <div style={{ padding: '6px 12px', fontSize: 12, color: '#778', background: '#fafbfc', borderBottom: '1px solid #f0f2f6' }}>
        Every event extracted from the case. Filter, then <strong>Add</strong> events into this chronology — events already in it show an “added” pill.
      </div>

      <div style={S.panelBody}>
        <div style={S.facetCol}>
          <div style={S.facetHdr}>Type</div>
          {facets.types.map((f) => (
            <FacetChip
              key={f.value} label={String(f.value).replace(/_/g, ' ').toLowerCase()} count={f.count}
              active={types.has(f.value)} onClick={() => toggleIn(types, setTypes, f.value)}
            />
          ))}
          <div style={S.facetHdr}>Participant</div>
          {presentableParticipants.slice(0, 80).map((f) => (
            <FacetChip
              key={f.value} label={f.value} count={f.count}
              active={participants.has(f.value)} onClick={() => toggleIn(participants, setParticipants, f.value)}
            />
          ))}
          {genericParticipants.length > 0 && (
            <button
              type="button"
              style={{ ...S.btn, fontSize: 11, padding: '3px 8px', marginTop: 4, color: '#778' }}
              onClick={() => setShowGenericParticipants((v) => !v)}
            >
              {showGenericParticipants ? 'Hide' : 'Show'} {genericParticipants.length} generic reference{genericParticipants.length === 1 ? '' : 's'}…
            </button>
          )}
          {showGenericParticipants && genericParticipants.map((f) => (
            <FacetChip
              key={f.value} label={f.value} count={f.count}
              active={participants.has(f.value)} onClick={() => toggleIn(participants, setParticipants, f.value)}
            />
          ))}
          {!facets.participants.length && <div style={{ fontSize: 12, color: '#99a' }}>No participants</div>}
        </div>

        <div style={S.evList}>
          {loading && <div style={{ padding: 16, color: '#778' }}>Loading events…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 16, color: '#778' }}>
              No events match the current filters.
              {(dateFrom || dateTo) && (
                <div style={{ marginTop: 8 }}>
                  A date range is set, which <strong>hides undated events</strong> — and most case events are undated.{' '}
                  <button style={S.btn} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear dates</button>
                </div>
              )}
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div style={{ ...S.evRow, fontWeight: 700, background: '#fafbfc', alignItems: 'center' }}>
              {missingCount > 0
                ? <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} title="Select all events not yet in this chronology" />
                : <span style={{ width: 16, display: 'inline-block' }} />}
              <span>
                {missingCount > 0 ? (
                  <>
                    {missingCount} of {filtered.length} not in this chronology yet —{' '}
                    <button type="button" style={S.linkBtn} onClick={toggleSelectAll}>
                      {allFilteredSelected ? 'clear selection' : 'select all'}
                    </button>, then Add
                  </>
                ) : (
                  `All ${filtered.length} event${filtered.length === 1 ? '' : 's'} shown ${filtered.length === 1 ? 'is' : 'are'} already in this chronology`
                )}
              </span>
            </div>
          )}
          {!loading && displayRows.map(({ ev, depth, kidCount }) => {
            const names = eventParticipantNames(ev);
            const expanded = expandedId === ev.event_id;
            return (
              <React.Fragment key={ev.event_id}>
                <label style={{ ...S.evRow, cursor: ev.already_in_chronology ? 'default' : 'pointer', opacity: ev.already_in_chronology ? 0.6 : 1, ...(depth ? { paddingLeft: 6 + depth * 26, borderLeft: '3px solid #e2d9f7' } : null) }}>
                  {ev.already_in_chronology
                    ? <span title="Already in this chronology" style={{ width: 16, textAlign: 'center', color: '#2c6e49', fontWeight: 700 }}>✓</span>
                    : <input type="checkbox" checked={selected.has(ev.event_id)} onChange={() => toggleSelect(ev)} />}
                  <div style={{ flex: 1 }}>
                    <div>
                      {ev.event_type && <Pill text={String(ev.event_type).replace(/_/g, ' ').toLowerCase()} color="#456" bg="#eef2fb" />}
                      {ev.already_in_chronology && <Pill text="added" color="#2c6e49" bg="#e6f4ec" />}
                      {kidCount > 0 && (
                        <span title="This event's time span covers the indented events below it (inferred from dates + shared participants — confirm on the chronology)">
                          <Pill text={`spans ${kidCount} · inferred`} color="#5b3fa8" bg="#efe8fd" />
                        </span>
                      )}
                      {' '}{ev.description || '(no description)'}
                    </div>
                    <div style={S.evMeta}>
                      {ev.occurred_at ? formatDate(ev.occurred_at, ev.occurred_at_date_only !== false) : 'Undated'}
                      {(ev.source_document_ids || []).length > 1 && (
                        <span
                          title="This event was independently extracted from multiple documents"
                          style={{ color: '#2c6e49', fontWeight: 600 }}
                        > · {ev.source_document_ids.length} sources</span>
                      )}
                      {(ev.source_document_ids || []).length === 1 && (
                        <span title="Extracted from a single document — deserves more scrutiny"> · single source</span>
                      )}
                      {names.length > 0 && <> · {names.join(', ')}</>}
                      {ev.filename && <> · {ev.filename}</>}
                    </div>
                  </div>
                  {onFetchDetail && ev.event_id && (
                    <button
                      type="button"
                      style={{ ...S.btn, fontSize: 11, padding: '2px 8px', alignSelf: 'flex-start' }}
                      title="See what this event is — full description, who's involved, and who says it happened — before adding it"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDetail(ev); }}
                    >{expanded ? '▾ details' : '▸ details'}</button>
                  )}
                </label>
                {expanded && <EventDetailPane detail={details[ev.event_id]} pathname={pathname} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div style={{ ...S.panelHead, borderTop: '1px solid #e6e9ef', borderBottom: 'none' }}>
        <button
          style={S.btnPrimary}
          disabled={busy || selected.size === 0}
          onClick={() => onAdd([...selected]).then(() => setSelected(new Set()))}
        >
          Add to chronology ({selected.size})
        </button>
        <span style={{ fontSize: 12, color: '#778' }}>
          Check the events you want on the left, then add them to this chronology.
        </span>
      </div>
    </div>
  );
}

/* ── Merge / Split / Place dialogs (portal-to-body, EntityDossierModal
 *    convention) ─────────────────────────────────────────────────────── */

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(20,24,32,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 10, padding: 18, width: 'min(680px, 94vw)', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' },
  head: { fontWeight: 700, fontSize: 15, marginBottom: 10 },
  label: { fontSize: 12, fontWeight: 700, color: '#667', margin: '10px 0 3px' },
  radioRow: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', fontSize: 13, cursor: 'pointer' },
};

function Modal({ children, onClose, helpRef }) {
  return createPortal(
    <div style={M.overlay} onClick={onClose}>
      <div style={M.modal} onClick={(e) => e.stopPropagation()} ref={helpRef}>{children}</div>
    </div>,
    document.body,
  );
}

/* Merge N selected cards. Two relations, chosen explicitly by the user:
 * coincide (same event, multiple accounts → one card, accounts preserved) or
 * contains (one card is a broad summary of the others → keep the specifics,
 * absorb the summary). Deterministic + free — the combined text defaults to
 * rule-based (the kept card's own text, editable here); no LLM call. */
function MergeDialog({ entries, onApply, onClose, busy }) {
  const helpRef = useHelpTarget({
    slug: 'merging-splitting-and-episodes',
    label: 'Merge entries',
    question: 'When should I merge chronology entries vs. make one an episode?',
  });
  const [relation, setRelation] = useState('coincide');
  const [targetId, setTargetId] = useState(entries[0]?.id);
  const [summaryId, setSummaryId] = useState(entries[0]?.id);
  // contains: 'nest' keeps the summary as an episode ABOVE the specifics
  // (display hierarchy, fully reversible); 'absorb' removes it into their
  // lineage. Nest is the default — it destroys nothing.
  const [containsMode, setContainsMode] = useState('nest');
  const target = entries.find((e) => e.id === targetId) || entries[0];
  const [title, setTitle] = useState(target?.title || '');
  const [description, setDescription] = useState(target?.description || '');
  const [dateChoice, setDateChoice] = useState('');

  const dateOptions = [];
  const seenDays = new Set();
  for (const e of entries) {
    if (!e.effective_date) continue;
    const day = String(e.effective_date).slice(0, 10);
    if (seenDays.has(day)) continue;
    seenDays.add(day);
    dateOptions.push({ day, label: `${formatDate(e.effective_date, e.date_only, e.date_precision)} (from “${e.title || 'untitled'}”)` });
  }

  const retarget = (id) => {
    setTargetId(id);
    const t = entries.find((e) => e.id === id);
    setTitle(t?.title || '');
    setDescription(t?.description || '');
  };

  const apply = () => {
    if (relation === 'contains') {
      onApply({
        relation: 'contains',
        contains_mode: containsMode,
        summary_entry_id: summaryId,
        child_entry_ids: entries.filter((e) => e.id !== summaryId).map((e) => e.id),
      });
    } else {
      onApply({
        relation: 'coincide',
        entry_ids: entries.map((e) => e.id),
        target_entry_id: targetId,
        merged: {
          title: title.trim() || null,
          description,
          ...(dateChoice ? { effective_date: `${dateChoice}T00:00:00Z`, date_only: true } : {}),
        },
      });
    }
  };

  return (
    <Modal onClose={onClose} helpRef={helpRef}>
      <div style={M.head}>Merge {entries.length} entries</div>
      <label style={M.radioRow}>
        <input type="radio" checked={relation === 'coincide'} onChange={() => setRelation('coincide')} />
        <span><strong>Same event, multiple accounts</strong> — combine into one card. Every original
          account is preserved on the card (expandable, independently citable) and the merge is undoable.</span>
      </label>
      <label style={M.radioRow}>
        <input type="radio" checked={relation === 'contains'} onChange={() => setRelation('contains')} />
        <span><strong>One is a broad summary of the others</strong> — keep the specific entries as the
          real timeline rows; fold the summary’s text and sources into them (undoable). The summary’s
          wide date never overwrites the specifics.</span>
      </label>

      {relation === 'coincide' ? (
        <>
          <div style={M.label}>Card to keep</div>
          {entries.map((e) => (
            <label key={e.id} style={M.radioRow}>
              <input type="radio" checked={targetId === e.id} onChange={() => retarget(e.id)} />
              <span>{e.title || '(untitled)'}{e.effective_date ? ` · ${formatDate(e.effective_date, e.date_only, e.date_precision)}` : ' · undated'}</span>
            </label>
          ))}
          <div style={M.label}>Combined title</div>
          <input style={{ ...S.input, width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} />
          <div style={M.label}>Combined description</div>
          <textarea style={{ ...S.input, width: '100%', minHeight: 70 }} value={description} onChange={(e) => setDescription(e.target.value)} />
          <div style={M.label}>Date</div>
          <select style={S.input} value={dateChoice} onChange={(e) => setDateChoice(e.target.value)}>
            <option value="">Earliest verified date (default)</option>
            {dateOptions.map((o) => <option key={o.day} value={o.day}>{o.label}</option>)}
          </select>
        </>
      ) : (
        <>
          <div style={M.label}>Which entry is the broad summary?</div>
          {entries.map((e) => (
            <label key={e.id} style={M.radioRow}>
              <input type="radio" checked={summaryId === e.id} onChange={() => setSummaryId(e.id)} />
              <span>{e.title || '(untitled)'}{e.effective_date ? ` · ${formatDate(e.effective_date, e.date_only, e.date_precision)}` : ' · undated'}</span>
            </label>
          ))}
          <div style={M.label}>What happens to the summary</div>
          <label style={M.radioRow}>
            <input type="radio" checked={containsMode === 'nest'} onChange={() => setContainsMode('nest')} />
            <span><strong>Keep it as an episode</strong> — the specifics nest under it (indented), it stays
              on the timeline as their container. Reversible with Un-nest.</span>
          </label>
          <label style={M.radioRow}>
            <input type="radio" checked={containsMode === 'absorb'} onChange={() => setContainsMode('absorb')} />
            <span><strong>Remove it</strong> — its text and sources fold into the specifics’ records and it
              leaves the timeline. Reversible with Unmerge.</span>
          </label>
        </>
      )}

      <div style={{ ...S.rowActions, marginTop: 14 }}>
        <button style={S.btnPrimary} onClick={apply} disabled={busy}>
          {relation !== 'contains' ? 'Merge'
            : containsMode === 'nest' ? 'Make episode' : 'Absorb summary'}
        </button>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Modal>
  );
}

/* Nest an entry under a broader episode (containment picker). */
function NestDialog({ entry, candidates, onApply, onClose, busy }) {
  const [parentId, setParentId] = useState(candidates[0]?.id || '');
  return (
    <Modal onClose={onClose}>
      <div style={M.head}>Nest “{entry.title || 'this entry'}” inside…</div>
      <div style={{ fontSize: 12.5, color: '#667', marginBottom: 8 }}>
        Display hierarchy only — nothing merges or deletes. The entry indents under the episode
        you pick and keeps its own date, sources, and citations. Un-nest any time.
      </div>
      <select style={{ ...S.input, width: '100%' }} value={parentId} onChange={(e) => setParentId(e.target.value)}>
        {candidates.map((a) => (
          <option key={a.id} value={a.id}>
            {(a.effective_date ? `${String(a.effective_date).slice(0, 10)} — ` : '(undated) — ')}{a.title || '(untitled)'}
          </option>
        ))}
      </select>
      <div style={{ ...S.rowActions, marginTop: 14 }}>
        <button style={S.btnPrimary} onClick={() => onApply(parentId)} disabled={busy || !parentId}>Nest</button>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Modal>
  );
}

function seedSplitParts(entry) {
  const text = (entry.description || '').trim();
  const paras = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  let chunks = paras.length >= 2 ? paras
    : (text.match(/[^.!?]+[.!?]+(?:\s|$)/g) || []).map((s) => s.trim()).filter(Boolean);
  if (chunks.length < 2) chunks = [text, ''];
  return chunks.slice(0, 6).map((c, i) => ({
    title: '', description: c, effective_date: '', date_quote: '', primary: i === 0,
  }));
}

/* Split one card into sequential parts. Part dates with a verbatim source
 * quote are verified server-side (date_source=inferred); undated parts are
 * auto-pinned after their previous sibling — a split narrative has known
 * internal order even when the sub-events are undated. */
function SplitDialog({ entry, onApply, onClose, busy }) {
  const [parts, setParts] = useState(() => seedSplitParts(entry));
  const [keepOriginal, setKeepOriginal] = useState(false);
  const setPart = (i, patch) => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const setPrimary = (i) => setParts((ps) => ps.map((p, j) => ({ ...p, primary: j === i })));
  const addPart = () => setParts((ps) => [...ps, { title: '', description: '', effective_date: '', date_quote: '', primary: false }]);
  const removePart = (i) => setParts((ps) => (ps.length > 2 ? ps.filter((_, j) => j !== i) : ps));
  const canApply = parts.length >= 2 && parts.every((p) => p.title.trim());

  const apply = () => onApply(
    parts.map((p) => ({
      title: p.title.trim(),
      description: p.description || null,
      effective_date: p.effective_date ? `${p.effective_date}T00:00:00Z` : null,
      date_quote: p.date_quote.trim() || null,
      primary: !!p.primary,
    })),
    keepOriginal,
  );

  return (
    <Modal onClose={onClose}>
      <div style={M.head}>Split “{entry.title || 'this entry'}” into parts</div>
      <div style={{ fontSize: 12.5, color: '#667', marginBottom: 8 }}>
        Sources carry to every part. Give a part a date plus the exact source quote that states it
        and the server verifies the quote; parts left undated keep their order automatically.
      </div>
      {parts.map((p, i) => (
        <div key={i} style={{ border: '1px solid #e6e9ef', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder={`Part ${i + 1} title (required)`}
              value={p.title}
              onChange={(e) => setPart(i, { title: e.target.value })}
            />
            <label style={{ fontSize: 12, whiteSpace: 'nowrap' }} title="This part keeps the link to the extracted graph event">
              <input type="radio" checked={p.primary} onChange={() => setPrimary(i)} /> primary
            </label>
            {parts.length > 2 && (
              <button style={S.btn} onClick={() => removePart(i)} title="Remove this part">✕</button>
            )}
          </div>
          <textarea
            style={{ ...S.input, width: '100%', minHeight: 44, marginTop: 6 }}
            placeholder="Description"
            value={p.description}
            onChange={(e) => setPart(i, { description: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input
              style={S.input}
              type="date"
              value={p.effective_date}
              onChange={(e) => setPart(i, { effective_date: e.target.value })}
              title="Optional — leave empty to keep this part undated (it stays in order after the previous part)"
            />
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder="Exact source quote stating this date (optional, verified)"
              value={p.date_quote}
              onChange={(e) => setPart(i, { date_quote: e.target.value })}
            />
          </div>
        </div>
      ))}
      <button style={S.btn} onClick={addPart}>+ Add part</button>
      <label style={{ display: 'block', fontSize: 12.5, marginTop: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={keepOriginal} onChange={(e) => setKeepOriginal(e.target.checked)} />{' '}
        Keep the original as a containing summary above these parts (instead of replacing it)
      </label>
      <div style={{ ...S.rowActions, marginTop: 14 }}>
        <button style={S.btnPrimary} onClick={apply} disabled={busy || !canApply}>Split</button>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Modal>
  );
}

/* Place an undated entry relative to another entry (partial-order pin). */
function PlaceDialog({ entry, anchors, onApply, onClose, busy }) {
  const [anchorId, setAnchorId] = useState(anchors[0]?.id || '');
  const [side, setSide] = useState('after');
  return (
    <Modal onClose={onClose}>
      <div style={M.head}>Place “{entry.title || 'this entry'}”</div>
      <div style={{ fontSize: 12.5, color: '#667', marginBottom: 8 }}>
        The entry stays undated but holds its position in the flow — shown with a 📌 chip and
        exported in place (“Undated — after: …”). Unpin any time to send it back to the tray.
      </div>
      <div style={M.label}>Position</div>
      <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
        <label style={{ cursor: 'pointer' }}>
          <input type="radio" checked={side === 'before'} onChange={() => setSide('before')} /> before
        </label>
        <label style={{ cursor: 'pointer' }}>
          <input type="radio" checked={side === 'after'} onChange={() => setSide('after')} /> after
        </label>
      </div>
      <div style={M.label}>Anchor entry</div>
      <select style={{ ...S.input, width: '100%' }} value={anchorId} onChange={(e) => setAnchorId(e.target.value)}>
        {anchors.map((a) => (
          <option key={a.id} value={a.id}>
            {(a.effective_date ? `${String(a.effective_date).slice(0, 10)} — ` : '(undated) — ')}{a.title || '(untitled)'}
          </option>
        ))}
      </select>
      <div style={{ ...S.rowActions, marginTop: 14 }}>
        <button style={S.btnPrimary} onClick={() => onApply(anchorId, side)} disabled={busy || !anchorId}>Place</button>
        <button style={S.btn} onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Modal>
  );
}

export default function ChronologyTab({ caseId }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const graphOn = !!profile?.features?.graph_explorer;
  const refineOn = !!profile?.features?.chronology_refine;
  const buildOn = !!profile?.features?.chronology_build;
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newOpen, setNewOpen] = useState(false);       // "New" name popover
  const [renaming, setRenaming] = useState(false);     // header title editor
  const [renameVal, setRenameVal] = useState('');
  const [showBrowser, setShowBrowser] = useState(false);
  const [browserEvents, setBrowserEvents] = useState([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Refine-with-Arc drawer: the id of the entry being refined (null = closed).
  // The drawer receives the LIVE entry from `detail` so an apply refreshes
  // the pinned snapshot in place.
  const [refineEntryId, setRefineEntryId] = useState(null);
  // Build-with-Arc lane (in-tab panel above the entry list).
  const [showBuild, setShowBuild] = useState(false);
  // Merge select-mode (checkboxes on cards → sticky "Merge (N)…" bar).
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  // Split / Place / Nest dialogs (id of the entry being acted on; null = closed).
  const [splitEntryId, setSplitEntryId] = useState(null);
  const [placeEntryId, setPlaceEntryId] = useState(null);
  const [nestEntryId, setNestEntryId] = useState(null);

  const loadList = useCallback(async () => {
    const data = await api.get(`/cases/${caseId}/chronologies`);
    setList(data.chronologies || []);
    return data.chronologies || [];
  }, [caseId]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    const d = await api.get(`/cases/${caseId}/chronologies/${id}`);
    setDetail(d);
  }, [caseId]);

  // Initial load: pick the newest chronology if one exists.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const rows = await loadList();
        if (cancelled) return;
        if (rows.length) { setActiveId(rows[0].id); await loadDetail(rows[0].id); }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load chronologies');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, loadList, loadDetail]);

  const run = useCallback(async (fn) => {
    setBusy(true); setError('');
    try { await fn(); }
    catch (e) { setError(e.message || 'Action failed'); }
    finally { setBusy(false); }
  }, []);

  // Optimistic mutation: apply the change to local entries immediately so the UI
  // feels instant, fire the API in the background, optionally reconcile with the
  // server's authoritative response on success, and resync from the server on
  // failure (so a rejected write never leaves the canvas in a wrong state).
  const applyOptimistic = useCallback((mutate) => {
    setDetail((d) => (d ? { ...d, entries: mutate(d.entries || []) } : d));
  }, []);

  const optimistic = useCallback((mutate, call, reconcile) => {
    setError(''); setNotice('');
    applyOptimistic(mutate);
    Promise.resolve().then(call).then((res) => {
      if (reconcile) applyOptimistic((es) => reconcile(es, res));
    }).catch((e) => {
      setError(e.message || 'Action failed');
      loadDetail(activeId);   // server is the source of truth on failure
    });
  }, [applyOptimistic, loadDetail, activeId]);

  const replaceEntry = (es, updated) => es.map((e) => (e.id === updated.id ? updated : e));

  const createChronology = () => run(async () => {
    const c = await api.post(`/cases/${caseId}/chronologies`, { title: newTitle.trim() || null });
    setNewTitle('');
    await loadList();
    setActiveId(c.id);
    setDetail(c);
  });

  // Rename the ACTIVE chronology via the existing PATCH (backend has carried
  // "Rename / re-describe" since day one — the UI just never called it, so
  // every chronology after the first was stuck "Untitled" forever).
  const renameChronology = () => run(async () => {
    const d = await api.patch(
      `/cases/${caseId}/chronologies/${activeId}`,
      { title: renameVal.trim() || null },
    );
    setRenaming(false);
    setDetail(d);      // PATCH returns the full detail response
    await loadList();  // picker labels show the new name
  });

  const selectChronology = async (id) => {
    setActiveId(id);
    setRenaming(false);
    await run(() => loadDetail(id));
  };

  const accept = (entry) => optimistic(
    (es) => es.map((e) => (e.id === entry.id ? { ...e, review_state: 'accepted' } : e)),
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/review`, { action: 'accept' }),
    replaceEntry,
  );
  const reject = (entry) => optimistic(
    (es) => es.map((e) => (e.id === entry.id ? { ...e, review_state: 'dismissed' } : e)),
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/review`, { action: 'dismiss' }),
  );
  const editEntry = (entry, patch) => {
    const optimisticEntry = {
      ...entry,
      ...(patch.title !== undefined ? { title: patch.title } : {}),
      ...(patch.note !== undefined ? { note: patch.note } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
      ...(patch.clear_date
        ? { effective_date: null, date_source: 'none' }
        : (patch.effective_date !== undefined
          ? { effective_date: patch.effective_date, date_only: patch.date_only ?? entry.date_only }
          : {})),
    };
    optimistic(
      (es) => es.map((e) => (e.id === entry.id ? optimisticEntry : e)),
      () => api.patch(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}`, patch),
      replaceEntry,
    );
  };
  const deleteEntry = (entry) => optimistic(
    (es) => es.filter((e) => e.id !== entry.id),
    () => api.del(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}`),
  );
  const addManual = () => optimistic(
    (es) => es,  // nothing to show until the server returns the real id
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries`, { title: 'New event', date_source: 'none' }),
    (es, created) => [...es, created],
  );
  const suggestDate = (entry) => run(async () => {
    setNotice('');
    const updated = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/suggest-placement`);
    await loadDetail(activeId);
    if (!(updated.suggested_dates || []).length) setNotice('No date found in this entry’s source text — set one manually via Edit.');
  });
  const suggestAll = () => run(async () => {
    setNotice('');
    const r = await api.post(`/cases/${caseId}/chronologies/${activeId}/suggest-placements`, {});
    await loadDetail(activeId);
    const n = r.scanned === 1 ? 'entry' : 'entries';
    setNotice(r.suggested > 0
      ? `Arc suggested dates for ${r.suggested} of ${r.scanned} undated ${n} — review and accept below.`
      : `No datable text found in the sources for ${r.scanned} undated ${n}.`);
  });
  const arcScan = (entry) => run(async () => {
    setNotice('');
    const updated = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/arc-suggest-date`);
    await loadDetail(activeId);
    const arcCount = (updated.suggested_dates || []).filter((c) => c.origin === 'arc').length;
    setNotice(arcCount > 0
      ? `Arc proposed ${arcCount === 1 ? 'a date' : `${arcCount} dates`} for “${updated.title || 'this entry'}” — each with its source quote. Pick one below.`
      : 'Arc read the sources but could not support a date for this entry — set one manually via Edit.');
  });
  const enrich = (entry) => run(async () => {
    setNotice('');
    await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/enrich`);
    await loadDetail(activeId);
    setNotice(refineOn
      ? 'Arc added notes to the card — apply what you want, or open ✨ Refine with Arc to work on the event together.'
      : 'Arc added notes to the card — review them, then apply what you want into the entry.');
  });
  // Applying a piece of Arc's notes also archives the panel — the user took
  // what they wanted; the notes collapse to a chip (kept, not deleted).
  const applyNotes = (entry, patch) => run(async () => {
    setNotice('');
    await api.patch(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}`, patch);
    await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/enrich/dismiss`);
    await loadDetail(activeId);
    setNotice('Applied to the entry — Arc’s notes archived on this card.');
  });
  const dismissNotes = (entry) => optimistic(
    (es) => es.map((e) => (e.id === entry.id && e.enrichment
      ? { ...e, enrichment: { ...e.enrichment, dismissed_at: new Date().toISOString() } }
      : e)),
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/enrich/dismiss`),
    replaceEntry,
  );
  const acceptPlacement = (entry, candidate) => optimistic(
    (es) => es.map((e) => (e.id === entry.id
      ? { ...e, effective_date: candidate.date, date_only: candidate.date_only, date_source: 'manual', suggested_dates: null }
      : e)),
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/placement`, {
      action: 'accept', date: candidate.date, date_only: candidate.date_only,
    }),
    replaceEntry,
  );
  const rejectPlacement = (entry) => optimistic(
    (es) => es.map((e) => (e.id === entry.id ? { ...e, suggested_dates: null } : e)),
    () => api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/placement`, { action: 'reject' }),
  );
  const acceptAll = () => {
    const suggested = (detail?.entries || []).filter((e) => e.review_state === 'suggested');
    optimistic(
      (es) => es.map((e) => (e.review_state === 'suggested' ? { ...e, review_state: 'accepted' } : e)),
      async () => {
        for (const e of suggested) {
          await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${e.id}/review`, { action: 'accept' });
        }
      },
    );
  };
  // ── Merge / split / pin / intra-day sequence (deterministic endpoints;
  //    each returns the full detail in canonical order — replace wholesale). ──
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); setMergeOpen(false); };
  const toggleSelect = (entry) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(entry.id)) n.delete(entry.id); else n.add(entry.id);
    return n;
  });
  const applyMerge = (payload) => run(async () => {
    let d;
    if (payload.relation === 'contains' && payload.contains_mode === 'nest') {
      // "Keep as episode": pure containment — the specifics nest under the
      // summary; nothing merges or deletes.
      d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/nest`, {
        parent_entry_id: payload.summary_entry_id,
        child_entry_ids: payload.child_entry_ids,
      });
    } else {
      const { contains_mode, ...body } = payload;
      d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/merge`, body);
    }
    setDetail(d);
    exitSelect();
    await loadList();
    setNotice(payload.relation !== 'contains'
      ? 'Entries merged — every original account is preserved on the card (Unmerge to undo).'
      : payload.contains_mode === 'nest'
        ? 'Episode created — the specific entries now nest under it (Un-nest to undo).'
        : 'Summary absorbed into its specific entries — Unmerge on any of them restores it.');
  });
  const applyNest = (entry, parentId) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/nest`, {
      parent_entry_id: parentId, child_entry_ids: [entry.id],
    });
    setDetail(d);
    setNestEntryId(null);
  });
  const unnestEntry = (entry) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/nest`, {
      parent_entry_id: null, child_entry_ids: [entry.id],
    });
    setDetail(d);
  });
  // One-button cleanup: block-then-judge episode pass as a background job.
  // Nesting is reversible, so it auto-applies and the user CULLS (Un-nest)
  // instead of approving each group.
  const organize = () => run(async () => {
    const r = await api.post(`/cases/${caseId}/chronologies/${activeId}/organize`);
    setNotice(r.deduped
      ? 'An organize pass is already running for this chronology — progress in the jobs banner.'
      : 'Organizing into episodes in the background — progress in the jobs banner. Entries nest as groups are classified; Un-nest anything Arc files wrong.');
  });
  const unmergeEntry = (entry) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/unmerge`);
    setDetail(d);
    await loadList();
    setNotice('Merge undone — the absorbed entries are back.');
  });
  const applySplit = (entry, parts, keepOriginal) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/split`, {
      parts, keep_original: keepOriginal,
    });
    setDetail(d);
    setSplitEntryId(null);
    await loadList();
    setNotice(`Split into ${parts.length} entries — undated parts keep their order automatically (Unsplit on any part to undo).`);
  });
  const unsplitEntry = (entry) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/unsplit`);
    setDetail(d);
    await loadList();
    setNotice('Split undone — the original entry is back.');
  });
  const applyPin = (entry, anchorId, side) => run(async () => {
    const d = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/pin`, {
      anchor_entry_id: anchorId, anchor_side: side,
    });
    setDetail(d);
    setPlaceEntryId(null);
  });
  const unpinEntry = (entry) => run(async () => {
    const d = await api.del(`/cases/${caseId}/chronologies/${activeId}/entries/${entry.id}/pin`);
    setDetail(d);
  });
  // Swap an entry with its neighbor within one day bucket (position = the
  // intra-day sequence; chronological order across days is inviolable).
  const moveWithin = (bucketItems, entry, dir) => {
    const ids = bucketItems.map((e) => e.id);
    const i = ids.indexOf(entry.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(async () => {
      const entries = await api.post(`/cases/${caseId}/chronologies/${activeId}/entries/reorder`, { entry_ids: ids });
      setDetail((d) => (d ? { ...d, entries } : d));
    });
  };

  const loadBrowserEvents = useCallback(async (id) => {
    setBrowserLoading(true);
    try {
      const d = await api.get(`/cases/${caseId}/chronologies/${id}/event-browser`, { limit: 3000 });
      setBrowserEvents(d.events || []);
    } finally {
      setBrowserLoading(false);
    }
  }, [caseId]);

  const openBrowser = () => {
    setShowBrowser(true);
    run(() => loadBrowserEvents(activeId));
  };
  const addSelected = (eventIds) => run(async () => {
    if (!eventIds.length) return;
    await api.post(`/cases/${caseId}/chronologies/${activeId}/add-from-events`, { event_ids: eventIds });
    await loadDetail(activeId);
    await loadBrowserEvents(activeId); // refresh already_in_chronology flags
  });
  // A build run just landed suggested entries (and maybe queued the Arc
  // date job) — refresh the canvas and the selector counts.
  const onBuildRan = useCallback((report) => {
    loadDetail(activeId);
    loadList();
    // Plan runs report `drafted`; source-drafted event adds report `added`.
    const n = report?.drafted ?? report?.added ?? 0;
    setNotice(n
      ? `Arc added ${n} suggested ${n === 1 ? 'entry' : 'entries'} — review below (accept, reject, or edit).`
      : 'Nothing new landed — everything matching is already in this chronology.');
  }, [loadDetail, loadList, activeId]);

  const fetchEventDetail = useCallback(
    (eventId) => api.get(`/cases/${caseId}/chronologies/${activeId}/event-browser/${eventId}`),
    [caseId, activeId],
  );

  const exportAs = (fmt) => run(async () => {
    const resp = await api.raw('GET', `/cases/${caseId}/chronologies/${activeId}/export.${fmt}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `chronology-${activeId}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  });

  const { groups, undated, childrenOf } = useMemo(
    () => partitionEntries(detail?.entries || []),
    [detail],
  );
  const suggestedCount = (detail?.entries || []).filter((e) => e.review_state === 'suggested').length;
  const titleById = useMemo(() => {
    const m = {};
    for (const e of detail?.entries || []) m[e.id] = e.title || '(untitled)';
    return m;
  }, [detail]);
  const selectedEntries = (detail?.entries || []).filter((e) => selectedIds.has(e.id));
  const splitEntryObj = splitEntryId
    ? (detail?.entries || []).find((e) => e.id === splitEntryId) || null : null;
  const placeEntryObj = placeEntryId
    ? (detail?.entries || []).find((e) => e.id === placeEntryId) || null : null;
  const nestEntryObj = nestEntryId
    ? (detail?.entries || []).find((e) => e.id === nestEntryId) || null : null;
  const flowAnchors = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  // Nest candidates: everything except the entry itself and its descendants.
  const nestCandidates = useMemo(() => {
    if (!nestEntryObj) return [];
    const excluded = new Set([nestEntryObj.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const e of detail?.entries || []) {
        if (e.parent_entry_id && excluded.has(e.parent_entry_id) && !excluded.has(e.id)) {
          excluded.add(e.id);
          grew = true;
        }
      }
    }
    return (detail?.entries || []).filter(
      (e) => !excluded.has(e.id) && e.review_state !== 'dismissed',
    );
  }, [nestEntryObj, detail]);
  const handlers = {
    pathname, onAccept: accept, onReject: reject, onDelete: deleteEntry, onEdit: editEntry,
    onSuggestDate: suggestDate, onAcceptPlacement: acceptPlacement, onRejectPlacement: rejectPlacement,
    onArcScan: arcScan, onEnrich: enrich,
    onApplyNotes: applyNotes, onDismissNotes: dismissNotes,
    onRefine: refineOn ? (entry) => setRefineEntryId(entry.id) : undefined,
    onSplit: (entry) => setSplitEntryId(entry.id),
    onUnsplit: unsplitEntry,
    onUnmerge: unmergeEntry,
    onPlace: (entry) => setPlaceEntryId(entry.id),
    onUnpin: unpinEntry,
    onNest: (entry) => setNestEntryId(entry.id),
    onUnnest: unnestEntry,
    selectMode, onToggleSelect: toggleSelect, titleById,
    busy, graphOn,
  };
  const refineEntry = refineEntryId
    ? (detail?.entries || []).find((e) => e.id === refineEntryId) || null
    : null;

  if (loading) return <div style={S.empty}>Loading…</div>;

  // No chronology yet — empty state.
  if (!activeId || !detail) {
    return (
      <div style={S.wrap}>
        <div style={S.empty}>
          <p>No chronology yet. Create one, then add events from the case to build your timeline.</p>
          <div style={{ ...S.bar, justifyContent: 'center' }}>
            <input style={S.input} placeholder="Chronology name (optional)" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            <button style={S.btnPrimary} onClick={createChronology} disabled={busy}>New chronology</button>
          </div>
          {error && <div style={S.err}>{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.bar}>
        {list.length > 1 && (
          <select style={S.input} value={activeId} onChange={(e) => selectChronology(e.target.value)}>
            {list.map((c) => (
              <option key={c.id} value={c.id}>{c.title || 'Untitled chronology'} ({c.entry_count})</option>
            ))}
          </select>
        )}
        {renaming ? (
          <>
            <input
              style={S.input}
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameVal.trim()) renameChronology();
                if (e.key === 'Escape') setRenaming(false);
              }}
              placeholder="Chronology name"
            />
            {/* PATCH treats a null title as "leave unchanged", so an empty
                rename would silently no-op — disable Save instead. */}
            <button style={S.btnPrimary} onClick={renameChronology} disabled={busy || !renameVal.trim()}>Save</button>
            <button style={S.btn} onClick={() => setRenaming(false)} disabled={busy}>Cancel</button>
          </>
        ) : (
          <strong
            style={{ fontSize: 15, cursor: 'pointer' }}
            title="Click to rename this chronology"
            onClick={() => { setRenameVal(detail.title || ''); setRenaming(true); }}
          >
            {detail.title || 'Untitled chronology'}
            <span style={{ fontSize: 12, color: '#98a0ae', marginLeft: 6 }}>✏ rename</span>
          </strong>
        )}
        <span style={{ flex: 1 }} />
        {buildOn && (
          <button
            style={showBuild ? S.btn : S.btnPrimary}
            onClick={() => setShowBuild((v) => !v)}
            disabled={busy}
            title="Describe the timeline you need — Arc scopes it against the case's events and proposes a plan you approve before anything is written"
          >✨ Build with Arc</button>
        )}
        <button style={buildOn ? S.btn : S.btnPrimary} onClick={openBrowser} disabled={busy}>🔎 Browse &amp; add events</button>
        {buildOn && (detail?.entries || []).filter((e) => e.review_state !== 'dismissed').length >= 6 && (
          <button
            style={S.btn}
            onClick={organize}
            disabled={busy}
            title="Arc groups same-day related entries and files specifics under their broad episode — runs in the background, fully reversible (Un-nest), small metered cost"
          >✨ Organize into episodes</button>
        )}
        {suggestedCount > 0 && <button style={S.btn} onClick={acceptAll} disabled={busy}>Accept all ({suggestedCount})</button>}
        {(detail?.entries || []).filter((e) => e.review_state !== 'dismissed').length >= 2 && (
          <button
            style={selectMode ? S.btnPrimary : S.btn}
            onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            disabled={busy}
            title="Select entries to merge — same event told twice, or a broad summary covering specifics"
          >{selectMode ? 'Done selecting' : '☑ Select'}</button>
        )}
        <button style={S.btn} onClick={addManual} disabled={busy}>+ Add entry</button>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button style={S.btn} onClick={() => setExportOpen((o) => !o)} disabled={busy}>⬇ Export ▾</button>
          {exportOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 19 }} onClick={() => setExportOpen(false)} />
              <div style={S.menu}>
                {[['docx', 'Word document (.docx)'], ['pdf', 'PDF'], ['csv', 'CSV (spreadsheet)']].map(([f, label]) => (
                  <button
                    key={f}
                    style={S.menuItem}
                    onClick={() => { setExportOpen(false); exportAs(f); }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            style={S.btn}
            onClick={() => { setNewTitle(''); setNewOpen((o) => !o); }}
            disabled={busy}
            title="Start another chronology for this case"
          >New</button>
          {newOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 19 }} onClick={() => setNewOpen(false)} />
              <div style={{ ...S.menu, padding: 10, display: 'flex', gap: 6, minWidth: 300 }}>
                <input
                  style={{ ...S.input, flex: 1 }}
                  autoFocus
                  placeholder="Chronology name (optional)"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { setNewOpen(false); createChronology(); }
                    if (e.key === 'Escape') setNewOpen(false);
                  }}
                />
                <button
                  style={S.btnPrimary}
                  onClick={() => { setNewOpen(false); createChronology(); }}
                  disabled={busy}
                >Create</button>
              </div>
            </>
          )}
        </div>
      </div>

      {error && <div style={S.err}>{error}</div>}
      {notice && <div style={{ fontSize: 13, color: '#2c6e49', margin: '8px 0' }}>{notice}</div>}
      {busy && <div style={{ fontSize: 12, color: '#778' }}>Working…</div>}

      {selectMode && (
        <div style={{ position: 'sticky', top: 0, zIndex: 15, background: '#eef5ff', border: '1px solid #bcd6ff', borderRadius: 8, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
          <span style={{ fontSize: 13 }}>
            {selectedIds.size ? `${selectedIds.size} selected` : 'Tick the entries to merge'}
          </span>
          <span style={{ flex: 1 }} />
          <button
            style={S.btnPrimary}
            disabled={busy || selectedIds.size < 2}
            onClick={() => setMergeOpen(true)}
          >Merge ({selectedIds.size})…</button>
          <button style={S.btn} onClick={exitSelect}>Cancel</button>
        </div>
      )}

      {/* Arc date pass (and any other case jobs) — progress + completion CTA. */}
      <BackgroundJobsBanner caseId={caseId} />

      {buildOn && showBuild && (
        <ChronologyBuildLane
          caseId={caseId}
          chronologyId={activeId}
          onClose={() => setShowBuild(false)}
          onRan={onBuildRan}
        />
      )}

      {showBrowser && (
        <EventBrowser
          events={browserEvents}
          loading={browserLoading}
          busy={busy}
          onAdd={addSelected}
          onClose={() => setShowBrowser(false)}
          onFetchDetail={fetchEventDetail}
          pathname={pathname}
        />
      )}

      {groups.length === 0 && undated.length === 0 && !showBuild && (
        <div style={S.empty}>
          <p>This chronology is empty.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {buildOn && (
              <button style={S.btnPrimary} onClick={() => setShowBuild(true)} disabled={busy}>
                ✨ Build with Arc
              </button>
            )}
            <button style={buildOn ? S.btn : S.btnPrimary} onClick={openBrowser} disabled={busy}>🔎 Browse &amp; add events</button>
          </div>
          <p style={{ fontSize: 12, marginTop: 10 }}>
            {buildOn && <>Describe the timeline you need and Arc drafts it for your review — or browse yourself: </>}
            pick the events you want from the case — select all to add everything, or just the key ones.
            Use <strong>+ Add entry</strong> for a manual event not in the documents.
          </p>
        </div>
      )}

      {groups.map((g) => {
        const datedItems = g.items.filter((e) => e.effective_date);
        const mover = datedItems.length > 1
          ? (entry, dir) => moveWithin(datedItems, entry, dir)
          : undefined;
        return (
          <div key={g.key}>
            <div style={S.dateHdr}>{g.label}</div>
            {g.items.map((e) => (
              <EntryNode
                key={e.id}
                entry={e}
                childrenOf={childrenOf}
                selectedIds={selectedIds}
                {...handlers}
                onMove={e.effective_date ? mover : undefined}
              />
            ))}
          </div>
        );
      })}

      <UndatedTray
        entries={undated}
        onSuggestAll={suggestAll}
        {...handlers}
        selectedIds={selectedIds}
        onMove={undated.length > 1 ? (entry, dir) => moveWithin(undated, entry, dir) : undefined}
      />

      {mergeOpen && selectedEntries.length >= 2 && (
        <MergeDialog
          entries={selectedEntries}
          busy={busy}
          onClose={() => setMergeOpen(false)}
          onApply={applyMerge}
        />
      )}
      {splitEntryObj && (
        <SplitDialog
          entry={splitEntryObj}
          busy={busy}
          onClose={() => setSplitEntryId(null)}
          onApply={(parts, keep) => applySplit(splitEntryObj, parts, keep)}
        />
      )}
      {placeEntryObj && flowAnchors.length > 0 && (
        <PlaceDialog
          entry={placeEntryObj}
          anchors={flowAnchors}
          busy={busy}
          onClose={() => setPlaceEntryId(null)}
          onApply={(aid, side) => applyPin(placeEntryObj, aid, side)}
        />
      )}
      {nestEntryObj && nestCandidates.length > 0 && (
        <NestDialog
          entry={nestEntryObj}
          candidates={nestCandidates}
          busy={busy}
          onClose={() => setNestEntryId(null)}
          onApply={(pid) => applyNest(nestEntryObj, pid)}
        />
      )}

      {refineOn && refineEntry && (
        <ChronologyRefineDrawer
          caseId={caseId}
          chronologyId={activeId}
          entry={refineEntry}
          onClose={() => setRefineEntryId(null)}
          onApplied={() => loadDetail(activeId)}
        />
      )}
    </div>
  );
}
